import type { Hazard } from '../db/types';

/** Origin of the tags generated for a locally created hazard. */
export type TagSource = 'server' | 'lexicon' | 'none';

/**
 * Build the `/api/handleSubmit` POST body for a hazard.
 *
 * PURE: no Dexie, no fetch, no side effects.
 *
 * The `tags` key is included ONLY when `hazard.tag_source === 'server'`. An
 * explicit tag list — even an empty one — suppresses the server's AI tagging,
 * so lexicon/none/undefined sources must leave the key absent entirely and let
 * the server classify the description itself.
 */
export function buildSubmitPayload(hazard: Hazard): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    id: hazard.id,
    description: hazard.description,
    latitude: hazard.latitude,
    longitude: hazard.longitude,
    image_data: hazard.image_data,
    utc_timestamp: hazard.utc_timestamp,
  };
  if (hazard.tag_source === 'server') {
    payload.tags = hazard.tags || [];
  }
  return payload;
}

/**
 * Convert a base64 `data:` URL to a File without fetch (atob + Uint8Array),
 * extracting the mime type from the data URL prefix.
 */
export function dataUrlToFile(dataUrl: string, filename: string): File {
  const commaIndex = dataUrl.indexOf(',');
  if (!dataUrl.startsWith('data:') || commaIndex === -1) {
    throw new Error('Invalid data URL: expected "data:<mime>[;base64],<payload>"');
  }
  const prefix = dataUrl.slice('data:'.length, commaIndex);
  const mimeType = prefix.endsWith(';base64') ? prefix.slice(0, -';base64'.length) : prefix;
  const binary = atob(dataUrl.slice(commaIndex + 1));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mimeType });
}

export const isSyncableMediaRef = (value: unknown): value is string => {
  return typeof value === 'string' &&
    value.length > 0 &&
    !value.startsWith('blob:') &&
    (value.startsWith('data:') || value.startsWith('http://') || value.startsWith('https://'));
};

export const normalizeMediaRefsForSync = (mediaRefs?: string[]): string[] => {
  if (!Array.isArray(mediaRefs)) {
    return [];
  }
  return mediaRefs.filter(isSyncableMediaRef);
};

export const mapWithConcurrency = async <T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> => {
  if (items.length === 0) {
    return [];
  }
  const cappedConcurrency = Math.max(1, Math.min(concurrency, items.length));
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const workers = Array.from({ length: cappedConcurrency }, async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex++;
      results[currentIndex] = await mapper(items[currentIndex], currentIndex);
    }
  });

  await Promise.all(workers);
  return results;
};

const MAX_MEDIA_BYTES = 100 * 1024 * 1024;

/** Ask the browser to persist storage and reject files that exceed the quota or the 100MB cap. */
export async function assertStorageForFiles(files: File[]): Promise<void> {
  for (const file of files) {
    if (file.size > MAX_MEDIA_BYTES) {
      throw new Error(`File "${file.name}" is too large. Max is 100MB.`);
    }
  }
  const storage = navigator.storage;
  if (!storage) {
    return;
  }
  if (storage.persist) {
    await storage.persist();
  }
  if (!storage.estimate) {
    return;
  }
  const estimate = await storage.estimate();
  const quota = estimate.quota ?? 0;
  const usage = estimate.usage ?? 0;
  const incoming = files.reduce((total, file) => total + file.size, 0);
  if (quota > 0 && usage + incoming > quota) {
    throw new Error('Not enough device storage for this media.');
  }
}
